import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef } from 'react'
import { useLatest } from 'react-use'
import type { ListImperativeAPI } from 'react-window'
import { RESULTS2_LIST_ROW_HEIGHT } from '../components/VirtualizedResults2List'
import type { RenderedListRow, Results2ListItem } from '../types'

type UseScrollSelectedRowIntoViewProps = {
  isLoading: boolean
  isTree: boolean
  items: Results2ListItem[]
  rendered: RenderedListRow[]
  renderedIndexById: Map<string, number>
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>
  selectedId?: string
  selectedIds?: string[]
  virtualListRef: MutableRefObject<ListImperativeAPI | undefined>
}

type UseScrollSelectedRowIntoViewResult = {
  /** Wire to the list's onRowsRendered — retries the scroll once the list has mounted. */
  handleRowsRendered: () => void
}

/**
 * Keeps the single selected row visible: expands its collapsed parent group if needed, then
 * scrolls the virtual list to the row. Rows have a fixed height, so a row's offset only changes
 * with its rendered index — deduplicating on (id, index) prevents re-scrolling on unrelated
 * re-renders (comment typing, drags) after the user scrolled elsewhere.
 */
export const useScrollSelectedRowIntoView = ({
  isLoading,
  isTree,
  items,
  rendered,
  renderedIndexById,
  setExpanded,
  selectedId,
  selectedIds,
  virtualListRef,
}: UseScrollSelectedRowIntoViewProps): UseScrollSelectedRowIntoViewResult => {
  const lastAutoScrollKeyRef = useRef<string | null>(null)

  const singleSelectedId =
    selectedIds && selectedIds.length === 1 ? selectedIds[0] : selectedId ?? null

  const itemsRef = useLatest(items)
  const isTreeRef = useLatest(isTree)

  // Expand a collapsed parent group once, when its child *becomes* the selection, so the child is
  // brought into view. Keyed on the selection only (items/isTree read through refs) — deliberately
  // not on `expanded` — so a subsequent manual collapse by the user is not immediately re-expanded.
  useEffect(() => {
    if (!singleSelectedId || !isTreeRef.current) return
    const parentId = itemsRef.current.find(i => i.id === singleSelectedId)?.parent
    if (!parentId) return
    setExpanded(state => (state[parentId] === false ? { ...state, [parentId]: true } : state))
  }, [singleSelectedId, itemsRef, isTreeRef, setExpanded])

  const scrollSelectedRowIntoView = useCallback(() => {
    if (isLoading || rendered.length === 0) return

    if (!singleSelectedId) {
      lastAutoScrollKeyRef.current = null
      return
    }

    const rowIndex = renderedIndexById.get(singleSelectedId)
    const listApi = virtualListRef.current
    if (rowIndex === undefined || !listApi) return

    const scrollKey = `${singleSelectedId}:${rowIndex}`
    if (lastAutoScrollKeyRef.current === scrollKey) return

    listApi.scrollToRow({ index: rowIndex, align: 'auto', behavior: 'instant' })

    // The scroll can run before react-window has measured its viewport (e.g. right after a tab
    // switch remounts the list). While the measured height is still 0, align:'auto' resolves to an
    // offset one row past the target, leaving the selected row just above the visible area. Only
    // remember the scroll as done once the row is genuinely in view; otherwise leave the dedup key
    // unset so the onRowsRendered retry re-runs it after the list has measured.
    const viewport = listApi.element
    const rowTop = rowIndex * RESULTS2_LIST_ROW_HEIGHT
    const rowInView =
      viewport != null &&
      viewport.clientHeight >= RESULTS2_LIST_ROW_HEIGHT &&
      rowTop >= viewport.scrollTop - 1 &&
      rowTop + RESULTS2_LIST_ROW_HEIGHT <= viewport.scrollTop + viewport.clientHeight + 1
    lastAutoScrollKeyRef.current = rowInView ? scrollKey : null
  }, [isLoading, rendered.length, singleSelectedId, renderedIndexById, virtualListRef])

  useEffect(() => {
    lastAutoScrollKeyRef.current = null
  }, [selectedId, selectedIds])

  useEffect(() => {
    if (!singleSelectedId) return

    // A child below a collapsed parent isn't rendered yet; the auto-expand effect above opens the
    // parent, which recomputes renderedIndexById and re-runs this scroll (a no-op until then).
    const raf = window.requestAnimationFrame(() => {
      scrollSelectedRowIntoView()
    })
    return () => window.cancelAnimationFrame(raf)
  }, [scrollSelectedRowIntoView, singleSelectedId])

  return { handleRowsRendered: scrollSelectedRowIntoView }
}
